import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import { ProjectWorkspaceModuleKey, User } from '../types';
import { useLanguage } from '../context/LanguageContext';
import { getVisibleProjectWorkspaceGroups } from './project-workspace/projectWorkspaceNavigation';

// ─── Icons ────────────────────────────────────────────────────────────────────

const WS_ICONS: Record<string, React.ReactNode> = {
  overview: (
    <svg className="w-[18px] h-[18px] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  ),
  timesheets: (
    <svg className="w-[18px] h-[18px] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2m-6 9l2 2 4-4" />
    </svg>
  ),
  workforce: (
    <svg className="w-[18px] h-[18px] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm10 10v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  production: (
    <svg className="w-[18px] h-[18px] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2zm0 0V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v10m-6 0a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2m0 0V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2z" />
    </svg>
  ),
  schedule: (
    <svg className="w-[18px] h-[18px] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3M4 11h16M6 5h12a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 15h3m2 0h3" />
    </svg>
  ),
  materials: (
    <svg className="w-[18px] h-[18px] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 7.5 12 3l8 4.5-8 4.5-8-4.5Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="m4 12 8 4.5 8-4.5M4 16.5 12 21l8-4.5" />
    </svg>
  ),
  'scope-areas': (
    <svg className="w-[18px] h-[18px] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 5.5 9 3l6 3 5-2.5v15L15 21l-6-3-5 2.5v-15Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 3v15m6-12v15" />
    </svg>
  ),
  'field-reports': (
    <svg className="w-[18px] h-[18px] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 12v4m0 0l-2-2m2 2l2-2" />
    </svg>
  ),
  'free-reports': (
    <svg className="w-[18px] h-[18px] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 4h10l4 4v12H5V4Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h8M8 13.5h8M8 17h5" />
    </svg>
  ),
  control: (
    <svg className="w-[18px] h-[18px] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 8v8M12 11v5M8 14v2M4 17h16" />
    </svg>
  ),
  documents: (
    <svg className="w-[18px] h-[18px] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 3h8l4 4v14H6V3Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M14 3v5h5M9 12h6M9 16h6" />
    </svg>
  ),
  drawings: (
    <svg className="w-[18px] h-[18px] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 5.5 9 3l6 3 5-2.5v15L15 21l-6-3-5 2.5v-15Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 3v15m6-12v15" />
    </svg>
  ),
  rfis: (
    <svg className="w-[18px] h-[18px] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 4h14v13H9l-4 4V4Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.5 9a2.5 2.5 0 1 1 3.2 2.4c-.7.25-1.2.7-1.2 1.6m0 2.5h.01" />
    </svg>
  ),
  'labor-budgets': (
    <svg className="w-[18px] h-[18px] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
    </svg>
  ),
  reports: (
    <svg className="w-[18px] h-[18px] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6M9 16h4" />
    </svg>
  ),
  setup: (
    <svg className="w-[18px] h-[18px] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 0 0-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 0 0-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 0 0-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 0 0-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 0 0 1.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
    </svg>
  ),
};

const ICON_BACK = (
  <svg className="w-[18px] h-[18px] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

// ─── Component ────────────────────────────────────────────────────────────────

export interface ProjectSidebarContentProps {
  projectId: string;
  projectName: string | null;
  user: User | null;
  collapsed: boolean;
  disabledModules?: ProjectWorkspaceModuleKey[];
}

export function ProjectSidebarContent({
  projectId,
  projectName,
  user,
  collapsed,
  disabledModules = [],
}: ProjectSidebarContentProps) {
  const { language } = useLanguage();
  const tx = (en: string, es: string) => (language === 'es' ? es : en);

  const visibleGroups = getVisibleProjectWorkspaceGroups(user, disabledModules);
  const visibleItems = visibleGroups.flatMap((group) => group.items);

  const projectInitial = projectName ? projectName[0].toUpperCase() : null;
  const projectLabel = projectName ?? `Project ${projectId}`;

  // ── Collapsed (72px) ────────────────────────────────────────────────────────
  if (collapsed) {
    return (
      <nav
        aria-label={tx('Project workspace navigation', 'Navegación del espacio del proyecto')}
        className="flex flex-col items-center gap-1 py-3 px-1"
      >
        {/* Back to Projects */}
        <Link
          to="/projects"
          title={tx('Back to Projects', 'Volver a Proyectos')}
          aria-label={tx('Back to Projects', 'Volver a Proyectos')}
          className="flex h-9 w-9 items-center justify-center rounded-[var(--radius-md)] text-slate-400 transition-colors duration-100 hover:bg-white/[0.06] hover:text-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-200 motion-reduce:transition-none"
        >
          {ICON_BACK}
        </Link>

        {/* Project initial */}
        <div
          title={projectLabel}
          className="w-9 h-9 rounded-lg bg-orange-500/15 flex items-center justify-center text-[13px] font-bold text-orange-400 mt-1 mb-1 flex-shrink-0"
        >
          {projectInitial === null ? (
            <div className="h-4 w-4 animate-pulse rounded bg-slate-700 motion-reduce:animate-none" />
          ) : (
            projectInitial
          )}
        </div>

        <div className="border-t border-slate-800 w-full my-1" />

        {/* Nav items — icons only with title tooltips */}
        {visibleItems.map((item) => {
          const label = tx(item.labelEn, item.labelEs);
          return (
            <NavLink
              key={item.path}
              to={`/projects/${projectId}/${item.path}`}
              title={label}
              aria-label={label}
              className={({ isActive }) =>
                [
                  'group flex h-9 w-9 items-center justify-center rounded-[var(--radius-md)] transition-colors duration-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-200 motion-reduce:transition-none',
                  isActive
                    ? 'bg-white/10 text-orange-400'
                    : 'text-slate-500 hover:bg-white/[0.06] hover:text-slate-300',
                ].join(' ')
              }
              style={({ isActive }) =>
                isActive ? { boxShadow: 'inset 2px 0 0 #F97316' } : {}
              }
            >
              {WS_ICONS[item.path]}
            </NavLink>
          );
        })}
      </nav>
    );
  }

  // ── Expanded (232px) ────────────────────────────────────────────────────────
  return (
    <nav
      aria-label={tx('Project workspace navigation', 'Navegación del espacio del proyecto')}
      className="flex flex-col py-3 px-3"
    >
      {/* Back to Projects */}
      <Link
        to="/projects"
        className="mb-1 flex min-h-10 items-center gap-2 rounded-[var(--radius-md)] px-3 py-2 text-sm font-medium text-slate-400 transition-colors duration-100 hover:bg-white/[0.06] hover:text-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-200 motion-reduce:transition-none"
      >
        {ICON_BACK}
        <span>{tx('Projects', 'Proyectos')}</span>
      </Link>

      {/* Project name / skeleton */}
      <div className="px-3 pb-3 border-b border-slate-800 mb-2">
        {projectName === null ? (
          <div className="space-y-1.5 mt-0.5">
            <div className="h-3.5 w-28 animate-pulse rounded bg-slate-700 motion-reduce:animate-none" />
            <div className="h-3 w-20 animate-pulse rounded bg-slate-800 motion-reduce:animate-none" />
          </div>
        ) : (
          <p
            className="text-[13px] font-semibold text-slate-100 leading-snug"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {projectName}
          </p>
        )}
      </div>

      {/* Permission-filtered module groups */}
      <div>
        {visibleGroups.map((group) => (
          <section key={group.key} className="mb-3 last:mb-0">
            <p className="mb-1 px-3 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-600">
              {tx(group.labelEn, group.labelEs)}
            </p>
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const label = tx(item.labelEn, item.labelEs);
                return (
                  <NavLink
                    key={item.path}
                    to={`/projects/${projectId}/${item.path}`}
                    className={({ isActive }) =>
                      [
                        'group flex min-h-10 items-center gap-3 rounded-[var(--radius-md)] px-3 py-2 text-sm font-medium transition-colors duration-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-200 motion-reduce:transition-none',
                        isActive
                          ? 'bg-white/10 text-white'
                          : 'text-slate-400 hover:bg-white/[0.06] hover:text-slate-100',
                      ].join(' ')
                    }
                    style={({ isActive }) =>
                      isActive ? { boxShadow: 'inset 3px 0 0 #F97316' } : {}
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span
                          className={`flex-shrink-0 transition-colors duration-100 ${
                            isActive
                              ? 'text-orange-400'
                              : 'text-slate-500 group-hover:text-slate-300'
                          }`}
                        >
                          {WS_ICONS[item.path]}
                        </span>
                        <span className="flex-1 truncate">{label}</span>
                        {isActive && <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-orange-400" />}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </nav>
  );
}
